import { callingCampaigns } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import type { AcquisitionData } from './acquisition'
import { audienceFilterSchema } from './audienceFilter'
import { servicePresetKeySchema } from './servicePreset'

// UTM identifiers stamped onto visits booked from a calling campaign. Kept
// as constants so the frontend, the historical-data backfill SQL, and the
// stats JSON query all agree on the spelling — drift between them silently
// turns "Käyntejä varattu" into 0.
export const CALLING_CAMPAIGN_UTM_SOURCE = 'call_campaign'
export const CALLING_CAMPAIGN_UTM_MEDIUM = 'outbound_call'

// Field name (under utmParams) for the numeric calling-campaign id.
// Deliberately namespaced as `call_campaign_id` rather than the generic
// `campaign_id` so future systems (email campaigns, marketing campaigns)
// can each carry their own id without colliding in the same jsonb query.
export const CALLING_CAMPAIGN_ID_PARAM = 'call_campaign_id'

// Builds the marketing-shaped acquisitionData for a visit booked off a
// calling campaign. Slots into visit.internalAcquisitionData via the
// existing VisitBookingContext.acquisitionData wiring; downstream
// `deriveSource` reads `utm_source` and surfaces the visit as a
// `call_campaign` channel in the broader attribution report.
export function buildCallingCampaignAcquisitionData(args: {
  campaignId: number
  campaignName: string
  timestamp?: string
}): AcquisitionData {
  return {
    timestamp: args.timestamp ?? new Date().toISOString(),
    utmParams: {
      utm_source: CALLING_CAMPAIGN_UTM_SOURCE,
      utm_medium: CALLING_CAMPAIGN_UTM_MEDIUM,
      utm_campaign: args.campaignName,
      [CALLING_CAMPAIGN_ID_PARAM]: String(args.campaignId),
    },
  }
}

export const PURCHASED_LIST_UTM_SOURCE = 'purchased_list'

// First-touch for a person the calling-list import creates: what brought them
// in was the bought list, keyed by batch. Deliberately not `call_campaign` —
// no campaign has picked the row up at import time, and a batch can feed
// several. First-touch reports then answer "what did buying lists earn us"
// while the call_campaign stamps above stay the per-event last touch.
export function buildPurchasedListAcquisitionData(args: {
  sourceBatch: string
  timestamp?: string
}): AcquisitionData {
  return {
    timestamp: args.timestamp ?? new Date().toISOString(),
    utmParams: {
      utm_source: PURCHASED_LIST_UTM_SOURCE,
      utm_campaign: args.sourceBatch,
    },
  }
}

export const DEFAULT_PRE_CALL_SMS_TEMPLATE =
  'Hei {customer.firstName}! {caller.firstName} Markku.fi:ltä soittaa sinulle pian numerosta {caller.phoneNumber}'

export interface PreCallSmsVars {
  customer: { firstName: string }
  caller: { firstName: string; phoneNumber: string }
}

// Whitelisted variable substitution. The cleanup pass scrubs artefacts that
// empty values leave behind ("Hei !" → "Hei!", double spaces).
export function renderPreCallSmsTemplate(
  template: string,
  vars: PreCallSmsVars,
): string {
  const lookup: Record<string, string> = {
    'customer.firstName': vars.customer.firstName,
    'caller.firstName': vars.caller.firstName,
    'caller.phoneNumber': vars.caller.phoneNumber,
  }
  const substituted = template.replace(/\{([a-zA-Z.]+)\}/g, (match, key) => {
    return key in lookup ? lookup[key] : match
  })
  return substituted
    .replace(/ +!/g, '!')
    .replace(/ +,/g, ',')
    .replace(/ +/g, ' ')
    .trim()
}

export const selectCallingCampaignSchema = createSelectSchema(callingCampaigns)
export const insertCallingCampaignSchema = createInsertSchema(
  callingCampaigns,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const upsertCallingCampaignSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  audienceFilter: audienceFilterSchema,
  preCallSmsTemplate: z.string().nullable().optional(),
  servicePresetKey: servicePresetKeySchema.nullable().optional(),
  queueMode: z.enum(['person', 'customer']).optional(),
})

export const phoneCallOutcomeSchema = z.enum([
  'no_answer',
  'reached_not_interested',
  'reached_callback_later',
  'booked_visit',
  'do_not_call',
  'unclear',
  'abandoned',
  'wrong_number',
])

export const phoneCallOutcomeSourceSchema = z.enum(['ai', 'operator', 'auto'])

// Reason modifier on callback_later dispositions. The UI prompts for it
// every time; nullable in storage only for rows written before this
// prompt existed.
export const phoneCallCallbackReasonSchema = z.enum([
  'no_answer',
  'customer_requested_later',
  'no_slots_available',
])

// A person we could call about a customer, in a customer-mode campaign.
// Contacts are people with a membership on the customer itself. Reaching a
// taloyhtiö through its managing agency is deliberately out of scope: only 675
// of the 2,728 agency-only taloyhtiöt have a named isännöitsijä on record, so
// for the rest the agency contact would be asked about a taloyhtiö they have
// no reason to know.
//
// `callable` and `available` are deliberately separate. Callable is a fact
// about the person (do we have a number). Available is a fact about this
// campaign (are they inside a no-answer cooldown, or past the attempt cap) —
// and it follows the person across every taloyhtiö they are a contact for,
// which matters because a few isännöitsijät are recorded on hundreds of them.
export const campaignContactSchema = z.object({
  userId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string().nullable(),
  email: z.string().nullable(),
  relationshipType: z
    .enum(['property_manager', 'board_chair', 'board', 'other'])
    .nullable(),
  rank: z.number(),
  callable: z.boolean(),
  available: z.boolean(),
  noAnswerAttempts: z.number(),
  lastNoAnswerAt: z.string().nullable(),
})

// Row shape returned by `callingCampaign.getQueue` — one lead per row.
export const callingQueueItemSchema = z.object({
  userId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  customerId: z.number().nullable(),
  customerName: z.string().nullable(),
  propertyId: z.number().nullable(),
  propertyAddress: z.string().nullable(),
  // Address from the purchased list, when the lead came from one. A cold lead
  // owns no property until booking creates one, so this is the only address
  // the row can show. Goes empty at the licence purge, by design.
  listAddress: z.string().nullable(),
  lastCallId: z.number().nullable(),
  lastCallAt: z.string().nullable(),
  lastCallOutcome: phoneCallOutcomeSchema.nullable(),
  lastCallOutcomeSource: phoneCallOutcomeSourceSchema.nullable(),
  lastCallAiSuggestedOutcome: phoneCallOutcomeSchema.nullable(),
  lastCallAiOutcomeConfidence: z.number().nullable(),
  lastCallAiSummary: z.string().nullable(),
  lastCallAiSuggestedCallbackAt: z.string().nullable(),
  lastCallCallbackAt: z.string().nullable(),
  lastCallCallbackReason: phoneCallCallbackReasonSchema.nullable(),
  lastCallOperatorNotes: z.string().nullable(),
  // Whether this lead came from a purchased calling list, which is what makes
  // the list address on this row trustworthy-but-unconfirmed rather than absent.
  fromCallingList: z.boolean(),
  // Per-campaign counters. Attempts count across the whole lifetime of this
  // lead in this campaign (no sliding window) — they drive the "already
  // called N times" warning in the callback modal and the no_answer cap that
  // auto-promotes to `abandoned`.
  noAnswerAttempts: z.number(),
  campaignAttempts: z.number(),
  // When the lead will re-surface in the calling queue. Set for
  // reached_callback_later (= callback_at) and for no_answer in cooldown
  // (= started_at + backoff interval). NULL for everyone else.
  nextRetryAt: z.string().nullable(),
})

// Row shape returned by `callingCampaign.getCustomerQueue` — one taloyhtiö per
// row. Deliberately not a variant of callingQueueItemSchema: the two queues
// have different units, and a shared type would be half-null in both
// directions.
export const customerQueueItemSchema = z.object({
  customerId: z.number(),
  customerName: z.string(),
  customerType: z.string(),
  propertyId: z.number().nullable(),
  propertyAddress: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
  // Tag ids come along so the workspace can edit them without refetching the
  // customer — the many-to-many upsert replaces the whole set, so preserving
  // the existing tags needs their ids.
  tags: z.array(
    z.object({ id: z.number(), name: z.string(), color: z.string() }),
  ),
  contacts: z.array(campaignContactSchema),
  // Latest call recorded against this customer in this campaign. `lastCallUserId`
  // is which contact was actually spoken to.
  lastCallId: z.number().nullable(),
  lastCallAt: z.string().nullable(),
  lastCallUserId: z.number().nullable(),
  lastCallOutcome: phoneCallOutcomeSchema.nullable(),
  lastCallOutcomeSource: phoneCallOutcomeSourceSchema.nullable(),
  lastCallAiSummary: z.string().nullable(),
  lastCallAiSuggestedCallbackAt: z.string().nullable(),
  lastCallCallbackAt: z.string().nullable(),
  lastCallCallbackReason: phoneCallCallbackReasonSchema.nullable(),
  lastCallOperatorNotes: z.string().nullable(),
  campaignAttempts: z.number(),
  // When the row returns to the queue: an operator-set callback, or the
  // earliest contact cooldown expiry.
  nextRetryAt: z.string().nullable(),
  // Every callable contact has hit the attempt cap. Derived, not stored —
  // there is no outcome row for it, because the cap belongs to the contact
  // while the outcome belongs to the taloyhtiö.
  contactsExhausted: z.boolean(),
})

// Lead-card context for a customer-mode row. Mirrors leadContextSchema's
// purpose, but everything hangs off the taloyhtiö rather than off a person —
// no linkedCustomers, because in customer mode the row already *is* the
// customership.
export const customerContextSchema = z.object({
  activeSubscriptions: z.array(
    z.object({
      id: z.number(),
      displayName: z.string(),
      status: z.string(),
    }),
  ),
  openOpportunities: z.array(
    z.object({
      id: z.number(),
      stage: z.string(),
      contextName: z.string().nullable(),
    }),
  ),
  // Every property the customer owns, oldest first — a paritalo's two halves,
  // a taloyhtiö's separate buildings. The queue row carries only the first one,
  // so the lead card reads the set from here: the visit history spans all of
  // them, and the operator has to see that there is more than one house to
  // talk about. `address` is a display string; the slot search takes the
  // structured `propertyAddress` above.
  properties: z.array(
    z.object({
      id: z.number(),
      address: z.string().nullable(),
      constructionYear: z.number().nullable(),
    }),
  ),
  // The agency managing this taloyhtiö (customers.managedByCustomerId), when
  // recorded — roughly a fifth of them. Null for households and for taloyhtiöt
  // whose manager we don't know.
  managingAgency: z.object({ id: z.number(), name: z.string() }).nullable(),
  // Notes the operator should read before dialling. Staff commentary only —
  // properties.notes is customer-visible and carries import provenance, which
  // is noise here. The `internal` prefix is load-bearing: omitInternalFields
  // strips these for non-staff roles.
  internalCustomerNotes: z.string().nullable(),
  internalPropertyNotes: z.string().nullable(),
  // Structured address of the taloyhtiö's property — the queue row carries a
  // display string, and the slot search needs the parts.
  propertyAddress: z
    .object({
      street: z.string(),
      postalCode: z.string().nullable(),
      city: z.string(),
    })
    .nullable(),
  // Completed visits at the property, newest first, reduced to their task
  // lines — `resolveServiceConfig` reads back the service config the property
  // was last booked with.
  recentPropertyVisits: z.array(
    z.object({
      visitId: z.number(),
      startTime: z.string().nullable(),
      tasks: z.array(
        z.object({
          maintenanceTaskId: z.number().nullable(),
          price: z.number().nullable(),
        }),
      ),
    }),
  ),
})

// Lead-card context: active subs + open opportunities + every customer the
// lead is linked to. An isännöitsijä is a member of their employing agency
// plus many managed taloyhtiöt — `linkedCustomers` carries all of them so the
// workspace shows the full portfolio, not just one. Past visits are fetched
// client-side via `visit.list`.
export const leadContextSchema = z.object({
  activeSubscriptions: z.array(
    z.object({
      id: z.number(),
      displayName: z.string(),
      status: z.string(),
    }),
  ),
  openOpportunities: z.array(
    z.object({
      id: z.number(),
      stage: z.string(),
      contextName: z.string().nullable(),
    }),
  ),
  linkedCustomers: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      type: z.string(),
      accessRole: z.enum(['owner', 'member']).nullable(),
      relationshipType: z
        .enum(['property_manager', 'board_chair', 'board', 'other'])
        .nullable(),
      postalCode: z.string().nullable(),
      city: z.string().nullable(),
      // Sales tags on the taloyhtiö, shown on the lead card so the operator
      // sees "ei tulisijoja" before dialling — not just on the next campaign's
      // audience exclusion.
      tags: z.array(z.object({ name: z.string(), color: z.string() })),
    }),
  ),
  // Properties across every linked customer — the visit history the lead card
  // renders, which is wider than the single property on the queue row.
  linkedPropertyIds: z.array(z.number()),
  // Every address the booking could target, for the workspace's Kohde
  // selector: one candidate per property across the lead's customerships,
  // plus the purchased list's address when it doesn't match any of them.
  // A matched existing customer can hold a property in another city than the
  // bought list covers — the seller picks, so both must be on offer. The
  // list candidate is unverified vendor data (badged on the card) and
  // disappears at the licence purge.
  serviceAddresses: z.array(
    z.object({
      source: z.enum(['property', 'calling_list']),
      propertyId: z.number().nullable(),
      customerId: z.number().nullable(),
      customerName: z.string().nullable(),
      // Null only on the calling_list candidate — a property always belongs
      // to a customer.
      customerType: z
        .enum(['household', 'housing_company', 'company', 'agency'])
        .nullable(),
      street: z.string(),
      postalCode: z.string().nullable(),
      city: z.string(),
      lastVisitAt: z.string().nullable(),
      constructionYear: z.number().nullable(),
    }),
  ),
})

// Completed visits at one property, newest first, reduced to their task
// lines — `resolveServiceConfig` reads back the service config the property
// was last booked with. Its own procedure so switching the Kohde selection
// only refetches this, not the whole lead context.
export const propertyVisitHistorySchema = z.array(
  z.object({
    visitId: z.number(),
    startTime: z.string().nullable(),
    tasks: z.array(
      z.object({
        maintenanceTaskId: z.number().nullable(),
        price: z.number().nullable(),
      }),
    ),
  }),
)

export type CallingCampaign = z.infer<typeof selectCallingCampaignSchema>
export type InsertCallingCampaign = z.infer<typeof insertCallingCampaignSchema>
export type UpsertCallingCampaign = z.infer<typeof upsertCallingCampaignSchema>
export type CallingQueueItem = z.infer<typeof callingQueueItemSchema>
export type CampaignContact = z.infer<typeof campaignContactSchema>
export type CustomerQueueItem = z.infer<typeof customerQueueItemSchema>
export type CustomerContext = z.infer<typeof customerContextSchema>
export type LeadContext = z.infer<typeof leadContextSchema>
export type PropertyVisitHistory = z.infer<typeof propertyVisitHistorySchema>
export type PhoneCallOutcome = z.infer<typeof phoneCallOutcomeSchema>
export type PhoneCallOutcomeSource = z.infer<
  typeof phoneCallOutcomeSourceSchema
>
export type PhoneCallCallbackReason = z.infer<
  typeof phoneCallCallbackReasonSchema
>
